import { useEffect, useState, useRef } from "react";

const W = 280, H = 110, PAD = { top: 12, right: 8, bottom: 8, left: 8 };

function smoothPath(pts) {
  if (!pts.length) return "";
  let d = `M ${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], c = pts[i];
    const cpx = (p.x + c.x) / 2;
    d += ` C ${cpx.toFixed(1)},${p.y.toFixed(1)} ${cpx.toFixed(1)},${c.y.toFixed(1)} ${c.x.toFixed(1)},${c.y.toFixed(1)}`;
  }
  return d;
}

export default function RatingChart({ history = [], currentRating }) {
  const pathRef = useRef(null);
  const [drawn, setDrawn] = useState(false);

  const data = history.length > 60 ? history.slice(-60) : history;

  const minR = data.length ? Math.min(...data) - 80 : 1400;
  const maxR = data.length ? Math.max(...data) + 80 : 2100;
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const pts = data.map((r, i) => ({
    x: PAD.left + (i / Math.max(data.length - 1, 1)) * chartW,
    y: PAD.top + (1 - (r - minR) / (maxR - minR)) * chartH,
  }));

  const linePath  = smoothPath(pts);
  const areaPath  = pts.length
    ? linePath +
      ` L ${pts[pts.length - 1].x.toFixed(1)},${(PAD.top + chartH).toFixed(1)}` +
      ` L ${pts[0].x.toFixed(1)},${(PAD.top + chartH).toFixed(1)} Z`
    : "";

  useEffect(() => {
    const t = setTimeout(() => setDrawn(true), 100);
    return () => clearTimeout(t);
  }, [history]);

  const pathLen = pathRef.current?.getTotalLength() ?? 600;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      {/* Current rating badge */}
      <div style={{ textAlign: "center" }}>
        <div style={{
          fontSize: 28,
          fontWeight: 700,
          fontFamily: "var(--font-display)",
          background: "linear-gradient(135deg, #c084fc, #60a5fa)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.03em",
          lineHeight: 1,
        }}>
          {currentRating != null ? currentRating.toLocaleString() : "—"}
        </div>
        <div style={{
          fontSize: 10,
          color: "rgba(148,163,184,0.45)",
          fontFamily: "var(--font-display)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginTop: 3,
        }}>
          Contest Rating
        </div>
      </div>

      {/* SVG chart */}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, overflow: "visible" }}>
        <defs>
          <linearGradient id="rg-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(192,132,252,0.25)" />
            <stop offset="100%" stopColor="rgba(192,132,252,0)"    />
          </linearGradient>
          <filter id="rg-glow">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Area fill */}
        {areaPath && (
          <path d={areaPath} fill="url(#rg-fill)" />
        )}

        {/* Line */}
        {linePath && (
          <path
            ref={pathRef}
            d={linePath}
            fill="none"
            stroke="#c084fc"
            strokeWidth={1.8}
            strokeLinecap="round"
            filter="url(#rg-glow)"
            strokeDasharray={pathLen}
            strokeDashoffset={drawn ? 0 : pathLen}
            style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1)" }}
          />
        )}

        {/* Last point dot */}
        {pts.length > 0 && drawn && (
          <circle
            cx={pts[pts.length - 1].x}
            cy={pts[pts.length - 1].y}
            r={3.5}
            fill="#c084fc"
            filter="url(#rg-glow)"
          />
        )}
      </svg>

      <div style={{
        fontSize: 10,
        color: "rgba(148,163,184,0.3)",
        fontFamily: "var(--font-display)",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}>
        Last {data.length} contests
      </div>
    </div>
  );
}
